import { useCallback, useMemo, useState } from 'react';
import { PAYER_LOAD_PAGE_SIZE } from '@features/payer-connections';

const NONE: ReadonlySet<string> = new Set();

/**
 * Lets at most PAYER_LOAD_PAGE_SIZE go-live status reads run at once, in row
 * order. A row may start once fewer than that many earlier rows are still
 * unsettled; a started row never stops, because settled rows only accumulate.
 * Settled rows belong to the visible page: a new page, or coming back to one,
 * starts the window over so stale cached rows cannot all refetch together.
 */
export function useGoLiveLoadWindow(organizationIds: string[]) {
  const pageKey = organizationIds.join('|');
  const [loadWindow, setLoadWindow] = useState<{ pageKey: string; settled: ReadonlySet<string> }>(
    () => ({
      pageKey,
      settled: NONE,
    }),
  );
  const settled = loadWindow.pageKey === pageKey ? loadWindow.settled : NONE;
  const markSettled = useCallback(
    (organizationId: string) => {
      setLoadWindow(previous => {
        const current = previous.pageKey === pageKey ? previous.settled : NONE;
        if (current.has(organizationId)) return previous;
        return { pageKey, settled: new Set(current).add(organizationId) };
      });
    },
    [pageKey],
  );
  const enabled = useMemo(() => {
    const allowed = new Set<string>();
    let unsettledBefore = 0;
    for (const organizationId of organizationIds) {
      if (settled.has(organizationId)) {
        allowed.add(organizationId);
        continue;
      }
      if (unsettledBefore < PAYER_LOAD_PAGE_SIZE) allowed.add(organizationId);
      unsettledBefore += 1;
    }
    return allowed;
  }, [organizationIds, settled]);
  return { enabled, markSettled };
}
